import React from "react";
import {
  Checkbox,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  UnitScanRow,
  UnitScanStatusKind,
} from "./OnvifScanUtils/cameraUnitScan";

const headerSx = { fontFamily: "Mulish", fontWeight: 700, color: "#001943" };
const cellSx = { fontFamily: "Mulish", color: "#283E6D" };

const STATUS_COLORS: Record<UnitScanStatusKind, string> = {
  ok: "#1B7F3B",
  warning: "#B26A00",
  error: "#C62828",
};

/**
 * Results of a camera unit scan, one row per EXISTING camera (keyed by camera
 * id): what the camera points at today, what the unit reports over ONVIF, and
 * the fields that would change on apply. Cameras without a suggestion cannot
 * be selected; their Change column says why in plain words.
 */
export const CameraUnitScanResultsTable = ({
  rows,
  selectedIds,
  onToggle,
  onToggleAll,
  disabled = false,
}: {
  rows: UnitScanRow[];
  selectedIds: Record<number, boolean>;
  onToggle: (id: number) => void;
  onToggleAll: (checked: boolean) => void;
  disabled?: boolean;
}) => {
  const selectable = rows.filter((row) => row.canApply);
  const selectedCount = selectable.filter((row) => selectedIds[row.id]).length;
  const allSelected =
    selectable.length > 0 && selectedCount === selectable.length;
  const someSelected = selectedCount > 0 && selectedCount < selectable.length;

  return (
    <TableContainer component={Paper} sx={{ maxHeight: "55vh" }}>
      <Table stickyHeader size="small" aria-label="camera unit scan results">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                disabled={disabled || selectable.length === 0}
                onChange={(e) => onToggleAll(e.target.checked)}
                inputProps={{ "aria-label": "select all cameras to update" }}
              />
            </TableCell>
            <TableCell sx={headerSx}>Camera</TableCell>
            <TableCell sx={headerSx}>Host</TableCell>
            <TableCell sx={headerSx}>Status</TableCell>
            <TableCell sx={headerSx}>Unit</TableCell>
            <TableCell sx={headerSx}>Current stream</TableCell>
            <TableCell sx={headerSx}>Suggested stream</TableCell>
            <TableCell sx={headerSx}>Login</TableCell>
            <TableCell sx={headerSx}>Change</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              hover
              selected={Boolean(selectedIds[row.id])}
              data-testid={`unit-scan-row-${row.id}`}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={Boolean(selectedIds[row.id])}
                  disabled={disabled || !row.canApply}
                  onChange={() => onToggle(row.id)}
                  inputProps={{ "aria-label": `select ${row.name}` }}
                />
              </TableCell>
              <TableCell sx={cellSx}>
                {row.name}
                {!row.active && (
                  <div style={{ fontSize: 11, color: "#7A7A7A" }}>inactive</div>
                )}
              </TableCell>
              <TableCell sx={cellSx}>{row.host || "—"}</TableCell>
              <TableCell
                sx={{ ...cellSx, color: STATUS_COLORS[row.statusKind] }}
              >
                {row.onvifError ? (
                  <Tooltip title={row.onvifError}>
                    <span>{row.statusText}</span>
                  </Tooltip>
                ) : (
                  row.statusText
                )}
              </TableCell>
              <TableCell sx={cellSx}>{row.unit}</TableCell>
              <TableCell
                sx={{ ...cellSx, fontFamily: "monospace", fontSize: 12 }}
              >
                {row.currentStream}
              </TableCell>
              <TableCell
                sx={{ ...cellSx, fontFamily: "monospace", fontSize: 12 }}
              >
                {row.suggestedStream}
              </TableCell>
              <TableCell sx={cellSx}>{row.login}</TableCell>
              <TableCell sx={cellSx}>
                {row.changeChips.length > 0 ? (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {row.changeChips.map((chip) => (
                      <Chip
                        key={chip}
                        label={chip}
                        size="small"
                        sx={{
                          fontFamily: "Mulish",
                          fontSize: 11,
                          backgroundColor: "#FFF3E0",
                          color: "#8A4B00",
                        }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <span
                    style={{
                      color: row.upToDate ? "#1B7F3B" : "#C62828",
                    }}
                  >
                    {row.changeText}
                  </span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
